// Google OAuth 2.0 configuration for web
// This is a simplified implementation for Google Sign-In on web

interface GoogleAuthConfig {
  clientId: string;
  scope?: string;
}

class GoogleAuth {
  private clientId: string;
  private scope: string;
  private scriptLoaded = false;

  constructor(config: GoogleAuthConfig) {
    this.clientId = config.clientId;
    this.scope = config.scope || 'email profile';
  }

  // Load Google Identity Services script
  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script'));
      };
      document.head.appendChild(script);
    });
  }

  // Initialize Google Sign-In
  async initialize(): Promise<void> {
    await this.loadScript();
    
    // Initialize Google Identity Services
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    }
  }

  // Handle the credential response
  private handleCredentialResponse(response: any) {
    // This will be overridden by the component
    console.log('Google credential response:', response);
  }

  // Render Google Sign-In button
  renderButton(elementId: string, callback: (credential: string) => void) {
    if (!window.google) {
      console.error('Google Identity Services not loaded');
      return;
    }

    // Set the callback
    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: any) => {
        if (response.credential) {
          callback(response.credential);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    // Render the button
    window.google.accounts.id.renderButton(
      document.getElementById(elementId),
      {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: '300',
      }
    );
  }

  // Prompt user to select Google account
  prompt(): void {
    if (window.google) {
      window.google.accounts.id.prompt();
    }
  }

  // Sign out
  signOut(): void {
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

// Create singleton instance
export const googleAuth = new GoogleAuth({
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  scope: 'email profile',
});

// Type declaration for Google Identity Services
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement | null, options: any) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}